import {
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  Table as TanStackTable,
  TableState,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import { useColumnResizingContext } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';

import { TablePaginationConfig, TableV2Meta } from '../types';

// Simplified hook that returns the TanStack table directly
export function useTableStore<T extends Record<string, any>>(options: {
  data: T[];
  columns: ColumnDef<T>[];
  getRowId?: (row: T, index: number) => string;
  enableMultiSort?: boolean;
  enableRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableSubRowSelection?: boolean | ((row: Row<T>) => boolean);
  pagination?: TablePaginationConfig;
  summaryData?: T | null;
  state?: Partial<TableState>;
  sortingFns?: Record<string, any>;
  meta?: Record<string, any>;
  onRowSelectionChange?: (updater: Updater<RowSelectionState>) => void;
  onExpandedChange?: (updater: any) => void; // For hierarchical data
  getSubRows?: (row: T) => T[]; // For hierarchical data
  getRowCanExpand?: (row: { original: T }) => boolean; // For hierarchical data
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  /**
   * If true, the pagination will be managed manually by the parent component.
   */
  manualPagination?: boolean;
  /**
   * The total number of rows in the table. If not provided, the table will calculate the total number of rows based on the data length.
   */
  rowCount?: number;
  /**
   * If true, column header filters will be enabled.
   */
  enableColumnFilters?: boolean;
}): TanStackTable<T> {
  const {
    data,
    columns,
    getRowId = (_, index) => String(index),
    enableMultiSort = true,
    enableRowSelection = true,
    enableMultiRowSelection = true,
    enableSubRowSelection = true,
    pagination,
    summaryData = null,
    state,
    sortingFns = {},
    meta,
    onRowSelectionChange,
    onExpandedChange,
    getSubRows,
    getRowCanExpand,
    onPaginationChange,
    onSortingChange,
    manualPagination = false,
    rowCount,
    enableColumnFilters = true,
  } = options;

  const { customColumnWidths, onColumnSizingChange } =
    useColumnResizingContext();

  // Create table with all features enabled
  const table = useReactTable<T>({
    autoResetPageIndex: false,
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel:
      pagination && !manualPagination ? getPaginationRowModel() : undefined,
    manualPagination,
    // When pagination is server-side, sorting is also server-side.
    // Without this, TanStack re-sorts server-returned data client-side using
    // state.sorting, overriding the sort order from OpenSearch (sortedGroupIds / sortedItemIds).
    manualSorting: manualPagination,
    // When server-side sorting is active (manualPagination=true, used by SalesTableFlattenedV2,
    // ListingTableFlattenedV2, and PurchaseTableV2 for Advanced Search v2), disable the "clear
    // sort" state in TanStack's asc → desc → clear cycle. Without this, the 3rd click on a
    // column header sends sort:null to the backend instead of toggling back to asc.
    enableSortingRemoval: !manualPagination,
    rowCount,
    getFilteredRowModel: getFilteredRowModel(),
    getSubRows,
    getRowCanExpand,
    autoResetExpanded: false,
    enableMultiSort,
    enableColumnFilters,
    enableColumnResizing: true,
    enableRowSelection,
    enableMultiRowSelection,
    enableSubRowSelection,
    columnResizeMode: 'onChange',
    sortingFns,
    filterFns: {},
    onRowSelectionChange,
    onExpandedChange,
    onColumnSizingChange,
    ...(onPaginationChange && { onPaginationChange }),
    ...(onSortingChange && { onSortingChange }),
    initialState: {
      pagination: {
        pageSize: pagination?.pageSize ?? 10,
      },
    },
    state: {
      ...state,
      columnSizing: customColumnWidths ?? {},
    },
    meta: {
      summaryData,
      columns,
      ...meta,
    } as TableV2Meta<T>,
  });

  return table;
}
